import { useState } from "react";
import { Loader2, PhoneCall, Video } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/auth-context";
import { useCalendarOptions, useCreateCallbackRequest } from "@/hooks/use-calendar";
import { CalendarEventDialog } from "./CalendarEventDialog";
import { ProjectPicker } from "@/components/calls/ProjectPicker";

interface CalendarCallDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CalendarCallDialog({ open, onOpenChange }: CalendarCallDialogProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const options = useCalendarOptions(open);
  const createRequest = useCreateCallbackRequest();
  const [mode, setMode] = useState<"callback" | "schedule">("callback");
  const [recipients, setRecipients] = useState<number[]>([]);
  const [projectId, setProjectId] = useState("");
  const [callType, setCallType] = useState<"audio" | "video">("video");
  const [topic, setTopic] = useState("");
  const selectedProject = options.data?.projects.find((project) => project.id === projectId);

  const toggleRecipient = (id: number, checked: boolean) => {
    setRecipients((previous) =>
      checked ? [...new Set([...previous, id])] : previous.filter((value) => value !== id),
    );
  };

  const submit = async () => {
    if (recipients.length === 0) {
      toast({ title: "Choose a recipient", description: "Select at least one active team member.", variant: "destructive" });
      return;
    }
    try {
      await createRequest.mutateAsync({
        recipientUserIds: recipients,
        projectId: projectId || null,
        callType,
        topic: topic.trim() || null,
      });
      toast({
        title: recipients.length === 1 ? "Callback requested" : "Callback requests sent",
        description: recipients.length > 1 ? "Each person received an independent request." : undefined,
      });
      setRecipients([]);
      setTopic("");
      onOpenChange(false);
    } catch (error: any) {
      toast({ title: "Could not request callback", description: error?.message, variant: "destructive" });
    }
  };

  if (mode === "schedule") {
    return (
      <CalendarEventDialog
        open={open}
        onOpenChange={(next) => {
          if (!next) setMode("callback");
          onOpenChange(next);
        }}
        initialKind="call"
      />
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-1rem)] max-w-md max-h-[92vh] overflow-y-auto bg-gray-800 border-gray-600 text-gray-100">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2"><PhoneCall className="h-4 w-4" />+ Call</DialogTitle>
          <DialogDescription className="text-xs text-gray-400">
            Request a callback now, or create a scheduled appointment without opening a room yet.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 rounded border border-gray-600 p-0.5">
          <Button type="button" size="sm" variant={mode === "callback" ? "default" : "ghost"} className="h-7 text-xs" onClick={() => setMode("callback")}>Callback</Button>
          <Button type="button" size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setMode("schedule")}>Schedule</Button>
        </div>
        <div className="space-y-3" data-testid="calendar-callback-form">
          {options.isError && (
            <div role="alert" className="rounded border border-red-500/40 bg-red-500/10 p-2 text-xs text-red-200">
              Active recipients and projects could not be loaded.
            </div>
          )}
          <fieldset className="border border-gray-700 rounded p-2">
            <legend className="px-1 text-xs text-gray-300">Recipients</legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-36 overflow-y-auto">
              {options.data?.users.filter((option) => option.id !== user?.id).map((option) => (
                <label key={option.id} className="flex items-center gap-2 rounded px-1 py-1 hover:bg-gray-700">
                  <Checkbox checked={recipients.includes(option.id)} onCheckedChange={(value) => toggleRecipient(option.id, value === true)} />
                  <span className="text-xs truncate">{option.name}</span>
                </label>
              ))}
            </div>
          </fieldset>
          <div className="space-y-1">
            <Label htmlFor="callback-project" className="text-xs">Project (optional)</Label>
            <ProjectPicker
              id="callback-project"
              testId="callback-project"
              value={projectId}
              onChange={setProjectId}
              selected={selectedProject ? { id: selectedProject.id, projectName: selectedProject.name, thumbnail: selectedProject.thumbnail } : null}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="callback-topic" className="text-xs">Topic (optional)</Label>
            <Input
              id="callback-topic"
              data-testid="callback-topic"
              value={topic}
              maxLength={200}
              placeholder="What is the call about?"
              className="h-8 text-xs"
              onChange={(event) => setTopic(event.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button type="button" size="sm" variant={callType === "video" ? "default" : "outline"} className="h-8" onClick={() => setCallType("video")}><Video className="h-3 w-3 mr-1" />Video</Button>
            <Button type="button" size="sm" variant={callType === "audio" ? "default" : "outline"} className="h-8" onClick={() => setCallType("audio")}><PhoneCall className="h-3 w-3 mr-1" />Audio</Button>
          </div>
          {recipients.length > 1 && (
            <p className="text-[11px] text-amber-200">Multiple recipients create separate callback requests, not a group call.</p>
          )}
        </div>
        <DialogFooter>
          <Button type="button" size="sm" onClick={submit} disabled={createRequest.isPending || options.isLoading || options.isError}>
            {createRequest.isPending && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
            Send request{recipients.length === 1 ? "" : "s"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}